import {
  PortalDayFragment,
  usePortalCompensationRequestCreateMutation,
  usePortalPeriodQuery,
  usePortalPeriodSignMutation,
  usePortalPunchesQuery,
} from "@dashboard/graphql";
import { Box, Button, Input, Text, Textarea } from "@saleor/macaw-ui-next";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { BalanceView } from "../BalanceView";
import { DayCard, ErrorText, KIND_MESSAGES, Page, Section } from "../components";
import { DailySign } from "../DailySign";
import { ExportButton } from "../ExportButton";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { SignaturePad } from "../SignaturePad";
import {
  addDays,
  downloadCsv,
  formatDateTime,
  formatDay,
  formatMinutes,
  isoDay,
  today,
  weekStart,
} from "../utils";

const Totals = ({
  period,
}: {
  period: {
    workedMinutes: number;
    contractedMinutes: number;
    balanceMinutes: number;
  };
}) => {
  const intl = useIntl();
  const items = [
    [m.registered, formatMinutes(period.workedMinutes)],
    [m.contracted, formatMinutes(period.contractedMinutes)],
    [m.balance, formatMinutes(period.balanceMinutes, true)],
  ] as const;

  return (
    <Box display="grid" gap={3} __gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))">
      {items.map(([label, value]) => (
        <Box key={label.defaultMessage as string} display="flex" flexDirection="column">
          <Text size={2} color="default2">
            {intl.formatMessage(label)}
          </Text>
          <Text size={6} fontWeight="bold">
            {value}
          </Text>
        </Box>
      ))}
    </Box>
  );
};

export const Records = () => {
  const intl = useIntl();
  const [start, setStart] = useState(() => addDays(weekStart(today()), -7));
  const end = addDays(start, 6);
  const { data, refetch } = usePortalPeriodQuery({
    variables: { start, end },
    fetchPolicy: "cache-and-network",
  });
  const punches = usePortalPunchesQuery({
    variables: { start, end },
    fetchPolicy: "cache-and-network",
  });
  const [agreed, setAgreed] = useState<boolean | null>(null);
  const [note, setNote] = useState("");
  const [drawing, setDrawing] = useState<string | null>(null);
  const [sign, signOpts] = usePortalPeriodSignMutation({
    onCompleted: result => {
      if (!result.periodSign?.errors.length) {
        setAgreed(null);
        setNote("");
        refetch();
      }
    },
  });

  const [comp, setComp] = useState({
    absenceDate: "",
    minutes: "30",
    compensationDate: "",
    compensationStart: "09:00",
    reason: "",
  });
  const [requestComp, compOpts] = usePortalCompensationRequestCreateMutation({
    onCompleted: result => {
      if (!result.compensationRequestCreate?.errors.length) {
        setComp({
          absenceDate: "",
          minutes: "30",
          compensationDate: "",
          compensationStart: "09:00",
          reason: "",
        });
        refetch();
      }
    },
  });

  // Every row of the record (corrections and voided punches included), by day.
  const days: PortalDayFragment[] = useMemo(() => {
    const all = punches.data?.myPunches ?? [];

    return (data?.myPeriod?.days ?? []).map(day => ({
      ...day,
      punches: all.filter(punch => isoDay(new Date(punch.effectiveAt)) === day.date),
    }));
  }, [data, punches.data]);

  const period = data?.myPeriod;
  // The digital time book starts on this day: nothing earlier is shown.
  const startDate = data?.timekeepingStartDate ?? null;

  useEffect(() => {
    if (startDate && end < startDate) {
      setStart(weekStart(startDate));
    }
  }, [startDate, end]);

  const closed = end < today();

  const exportCsv = () =>
    downloadCsv(`registo-ponto-${start}.csv`, [
      [
        intl.formatMessage(l.csvDate),
        intl.formatMessage(l.csvTime),
        intl.formatMessage(l.csvKind),
        intl.formatMessage(l.csvSeq),
        intl.formatMessage(l.csvHash),
        intl.formatMessage(l.csvCorrection),
        intl.formatMessage(l.csvReason),
        intl.formatMessage(l.csvFlags),
      ],
      ...(punches.data?.myPunches ?? []).map(punch => [
        isoDay(new Date(punch.effectiveAt)),
        formatDateTime(punch.effectiveAt),
        intl.formatMessage(KIND_MESSAGES[punch.kind]),
        punch.seq,
        punch.hash,
        punch.isCorrection ? intl.formatMessage(punch.voids ? l.csvVoid : l.csvYes) : "",
        punch.reason,
        punch.flags.join(" "),
      ]),
    ]);

  return (
    <Page>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" className="portal-no-print">
        <Button
          variant="secondary"
          size="small"
          disabled={!!startDate && start <= weekStart(startDate)}
          onClick={() => setStart(addDays(start, -7))}
        >
          ‹ {intl.formatMessage(m.previous)}
        </Button>
        <Text size={6} fontWeight="bold">
          {intl.formatMessage(m.week, { date: `${formatDay(start)} – ${formatDay(end)}` })}
        </Text>
        <Button
          variant="secondary"
          size="small"
          disabled={addDays(start, 7) > today()}
          onClick={() => setStart(addDays(start, 7))}
        >
          {intl.formatMessage(m.next)} ›
        </Button>
        <Box __flex={1} />
        <Button variant="tertiary" size="small" onClick={exportCsv}>
          {intl.formatMessage(m.downloadCsv)}
        </Button>
        <Button variant="tertiary" size="small" onClick={() => window.print()}>
          {intl.formatMessage(m.print)}
        </Button>
        <ExportButton />
      </Box>
      {period && (
        <Section>
          <Totals period={period} />
        </Section>
      )}
      {data?.myBalance && (
        <Section title={intl.formatMessage(m.balanceTitle)}>
          <BalanceView balance={data.myBalance} />
        </Section>
      )}
      <Section>
        {days.map(day => (
          <Box key={day.date} display="flex" flexDirection="column" gap={2}>
            <DayCard
              day={day}
              worker
              onChanged={() => {
                refetch();
                punches.refetch();
              }}
            />
            {data?.myPendingDays.includes(day.date) && (
              <DailySign
                day={data.myPeriod!.days.find(d => d.date === day.date)!}
                onDone={() => refetch()}
              />
            )}
          </Box>
        ))}
      </Section>
      <Section title={intl.formatMessage(m.compensationTitle)}>
        <Box
          display="grid"
          gap={2}
          __gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))"
          className="portal-no-print"
        >
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.missingDay)}
            value={comp.absenceDate}
            onChange={e => setComp({ ...comp, absenceDate: e.target.value })}
          />
          <Input
            size="small"
            type="number"
            min={5}
            label={intl.formatMessage(m.minutes)}
            value={comp.minutes}
            onChange={e => setComp({ ...comp, minutes: e.target.value })}
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.compensationDay)}
            value={comp.compensationDate}
            onChange={e => setComp({ ...comp, compensationDate: e.target.value })}
          />
          <Input
            size="small"
            type="time"
            label={intl.formatMessage(m.start)}
            value={comp.compensationStart}
            onChange={e => setComp({ ...comp, compensationStart: e.target.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(m.reason)}
            value={comp.reason}
            maxLength={2000}
            onChange={e => setComp({ ...comp, reason: e.target.value })}
          />
        </Box>
        <Box className="portal-no-print">
          <Button
            size="small"
            variant="primary"
            disabled={
              !comp.absenceDate || !comp.compensationDate || !comp.reason.trim() || compOpts.loading
            }
            onClick={() => requestComp({ variables: { ...comp, minutes: Number(comp.minutes) } })}
          >
            {intl.formatMessage(m.send)}
          </Button>
        </Box>
        <ErrorText errors={compOpts.data?.compensationRequestCreate?.errors} />
        {data?.myCompensationRequests.map(item => (
          <Text key={item.id} size={2}>
            {formatDay(item.absenceDate)} → {formatDay(item.compensationDate)} ·{" "}
            {formatMinutes(item.minutes)} · {item.reason} — {item.status.toLowerCase()}
            {item.decisionNote && ` (${item.decisionNote})`}
          </Text>
        ))}
      </Section>
      {(data?.myMonthApprovals.length ?? 0) > 0 && (
        <Section title={intl.formatMessage(m.monthApprovals)}>
          {data?.myMonthApprovals.map(approval => (
            <Text key={approval.id} size={2}>
              {approval.month.slice(0, 7)} ·{" "}
              {intl.formatMessage(m.by, { name: approval.approvedByName })} ·{" "}
              {formatDateTime(approval.approvedAt)} · {approval.summaryHash.slice(0, 16)}
            </Text>
          ))}
        </Section>
      )}
      <Section title={intl.formatMessage(m.signWeek)}>
        {period?.signature ? (
          <Text size={3}>
            {intl.formatMessage(m.signed, {
              date: formatDateTime(period.signature.signedAt),
              status: intl.formatMessage(period.signature.agreed ? m.agreed : m.disagreed),
              hash: period.signature.summaryHash.slice(0, 16),
            })}
            {period.signature.note && ` — “${period.signature.note}”`}
          </Text>
        ) : !closed ? (
          <Text size={2} color="default2">
            {intl.formatMessage(m.weekOpen)}
          </Text>
        ) : (
          <Box display="flex" flexDirection="column" gap={3} className="portal-no-print">
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant={agreed === true ? "primary" : "secondary"}
                onClick={() => setAgreed(true)}
              >
                {intl.formatMessage(m.agree)}
              </Button>
              <Button
                variant={agreed === false ? "primary" : "secondary"}
                onClick={() => setAgreed(false)}
              >
                {intl.formatMessage(m.disagree)}
              </Button>
            </Box>
            <Textarea
              rows={3}
              maxLength={4000}
              label={intl.formatMessage(m.signNote)}
              value={note}
              onChange={event => setNote(event.target.value)}
            />
            <SignaturePad key={start} onChange={setDrawing} />
            <Box>
              <Button
                variant="primary"
                disabled={
                  agreed === null ||
                  (agreed === false && !note.trim()) ||
                  !drawing ||
                  signOpts.loading
                }
                onClick={() =>
                  sign({
                    variables: {
                      start,
                      end,
                      agreed: agreed === true,
                      note: note.trim(),
                      drawing: drawing ?? "",
                    },
                  })
                }
              >
                {intl.formatMessage(m.signWeek)}
              </Button>
            </Box>
            <ErrorText errors={signOpts.data?.periodSign?.errors} />
          </Box>
        )}
      </Section>
    </Page>
  );
};
